import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Hidden5SReport from './Hidden5SReport';

// ── MATRIX CONSTANTS ──────────────────────────────────────────────────────────
const FACTORS_MATRIX = {
  "Signaling":          ["1A","2D","3C","4B","5C","6B","7A","8C","9C","10B","11A","12C"],
  "Stimulating":        ["1B","2C","3D","4A","5D","6A","7B","8D","9D","10A","11B","12D"],
  "Suggesting":         ["1C","2A","3A","4D","5B","6D","7C","8B","9B","10D","11C","12A"],
  "Sharing_Sanctioning":["1D","2B","3B","4C","5A","6C","7D","8A","9A","10C","11D","12B"],
};

const ADAPTABILITY_MATRIX = {
  1:  { A: 2,  B: -1, C: 1,  D: -2 },
  2:  { A: 2,  B: -2, C: 1,  D: -1 },
  3:  { A: 1,  B: -1, C: -2, D: 1  },
  4:  { A: 1,  B: -2, C: 2,  D: -1 },
  5:  { A: -2, B: 1,  C: 2,  D: -1 },
  6:  { A: -1, B: 1,  C: -2, D: 2  },
  7:  { A: -2, B: 2,  C: -1, D: 1  },
  8:  { A: 2,  B: -1, C: -2, D: 1  },
  9:  { A: -2, B: 1,  C: 2,  D: -1 },
  10: { A: 1,  B: -2, C: -1, D: 2  },
  11: { A: -2, B: 2,  C: -1, D: 1  },
  12: { A: -1, B: 2,  C: -2, D: 1  },
};

// ── STYLE COLOUR TOKENS ───────────────────────────────────────────────────────
// Keys must match the dominantStyle strings produced by calculate5S
const PILL_TOKENS = {
  "signaling":            { bg: "#f0f4ff", color: "#3b5ec0", border: "#bfdbfe" },
  "stimulating":          { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  "suggesting":           { bg: "#fffbeb", color: "#d9790a", border: "#fde68a" },
  "sharing & sanctioning":{ bg: "#faf5ff", color: "#7c3aed", border: "#e9d5ff" },
};

// ── Hex colour helpers ────────────────────────────────────────────────────────
const hexToRgb = (h) => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
const rgbToHex = ([r, g, b]) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
const avgHex = (hexes) => {
  const channels = hexes.map(hexToRgb);
  return rgbToHex([0, 1, 2].map((i) => channels.reduce((s, c) => s + c[i], 0) / channels.length));
};

// ── DominantStylePill ─────────────────────────────────────────────────────────
const DominantStylePill = ({ dominantStyle }) => {
  if (!dominantStyle) return <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>;

  const key = dominantStyle.toLowerCase();
  const c   = PILL_TOKENS[key] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };

  return (
    <span style={{
      padding:      "4px 12px",
      borderRadius: "999px",
      fontSize:     "12px",
      fontWeight:   "800",
      whiteSpace:   "nowrap",
      display:      "inline-block",
      lineHeight:   "1.6",
      background:   c.bg,
      color:        c.color,
      border:       `1px solid ${c.border}`,
    }}>
      {dominantStyle}
    </span>
  );
};

// ── Score pill colours (same tokens) ─────────────────────────────────────────
const SCORE_COLORS = {
  Signaling:           PILL_TOKENS["signaling"],
  Stimulating:         PILL_TOKENS["stimulating"],
  Suggesting:          PILL_TOKENS["suggesting"],
  Sharing_Sanctioning: PILL_TOKENS["sharing & sanctioning"],
  Total_Adaptability:  { bg: "#f8f0ff", color: "#6d28d9", border: "#ddd6fe" },
};

// ── calculate5S ───────────────────────────────────────────────────────────────
const calculate5S = (answers) => {
  if (!answers || answers.length === 0) return null;

  const scores = {
    Signaling: 0, Stimulating: 0, Suggesting: 0,
    Sharing_Sanctioning: 0, Total_Adaptability: 0,
  };

  answers.forEach((ans) => {
    if (!ans.question_no || !ans.selected_answer) return;
    const rawAnswer    = String(ans.selected_answer).replace(/^"|"$/g, "").trim();
    const selectedLetter = rawAnswer.charAt(0).toUpperCase();
    const qNo          = ans.question_no;
    const comboKey     = `${qNo}${selectedLetter}`;

    if (FACTORS_MATRIX["Signaling"].includes(comboKey))           scores.Signaling++;
    if (FACTORS_MATRIX["Stimulating"].includes(comboKey))         scores.Stimulating++;
    if (FACTORS_MATRIX["Suggesting"].includes(comboKey))          scores.Suggesting++;
    if (FACTORS_MATRIX["Sharing_Sanctioning"].includes(comboKey)) scores.Sharing_Sanctioning++;

    if (ADAPTABILITY_MATRIX[qNo]?.[selectedLetter] !== undefined) {
      scores.Total_Adaptability += ADAPTABILITY_MATRIX[qNo][selectedLetter];
    }
  });

  const styleKeys = ["Signaling", "Stimulating", "Suggesting", "Sharing_Sanctioning"];
  let dominantStyle = styleKeys[0];
  let maxScore      = scores[styleKeys[0]];
  styleKeys.forEach((style) => {
    if (scores[style] > maxScore) { maxScore = scores[style]; dominantStyle = style; }
  });

  const displayName =
    dominantStyle === "Sharing_Sanctioning" ? "Sharing & Sanctioning" : dominantStyle;

  return {
    dominantStyle:    displayName,
    totalAdaptability: scores.Total_Adaptability,
    maxScore,
    rawScores:        scores,
  };
};

// ═════════════════════════════════════════════════════════════════════════════
export default function Global5SResponses({ surveyId }) {
  const [responses,       setResponses]       = useState([]);
  const [loading,         setLoading]         = useState(true);

  // ── Filters ──
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterStyle,     setFilterStyle]     = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (surveyId) fetchResponses();
  }, [surveyId]);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/superadmin/survey/${surveyId}/global-responses`;
      const res = await axios.get(url);
      setResponses(res.data || []);
    } catch (err) {
      console.error("Failed to fetch global survey responses", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────
  // Pre-compute results once so we can build counts, unique lists, etc.
  const enriched = responses.map((resp) => ({
    ...resp,
    _results: calculate5S(resp.answers),
  }));

  const uniqueStyles = [
    ...new Set(enriched.map((r) => r._results?.dominantStyle || "").filter(Boolean)),
  ].sort();

  const uniqueCompanies = [
    ...new Set(enriched.map((r) => (r.custom_company || "").trim()).filter(Boolean)),
  ].sort();

  const styleCounts = enriched.reduce((acc, r) => {
    const key = r._results?.dominantStyle || "";
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // ── Filter ────────────────────────────────────────────────────────────────
  const filteredData = enriched.filter((resp) => {
    const matchName    = (resp.name    || "").toLowerCase().includes(filterName.toLowerCase());
    const matchEmail   = (resp.email   || "").toLowerCase().includes(filterEmail.toLowerCase());
    const matchCompany =
      filterCompany === "" ||
      (resp.custom_company || "").trim().toLowerCase() === filterCompany.toLowerCase();
    const matchStyle   =
      filterStyle === "" ||
      (resp._results?.dominantStyle || "").toLowerCase() === filterStyle.toLowerCase();

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(resp.submission_time);
      submissionDate.setHours(0, 0, 0, 0);
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchStyle && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterStyle("");
    setFilterCompany("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── PDF download ──────────────────────────────────────────────────────────
  const handleDownloadPDF = (participant, results, rowIndex) => {
    setIsGeneratingRow(rowIndex);
    setDownloadData({
      name:            participant.name,
      email:           participant.email,
      phone:           participant.phone,
      submission_time: participant.submission_time,
      scores:          results.rawScores,
    });
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: "20px", color: "#888" }}>Loading 5S data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>

      {/* Hidden PDF renderer */}
      <Hidden5SReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "15px",
      }}>
        <h3 style={{ margin: 0 }}>🎯 5S Leadership Styles ({filteredData.length})</h3>
        <span style={{
          background: "#0f2744", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Dominant-style summary pills (clickable filter) ── */}
      {enriched.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {Object.entries(styleCounts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([style, count]) => {
              const key      = style.toLowerCase();
              const c        = PILL_TOKENS[key] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
              const isActive = filterStyle.toLowerCase() === style.toLowerCase();
              return (
                <span
                  key={style}
                  onClick={() => setFilterStyle(isActive ? "" : style)}
                  title={`Click to filter by ${style}`}
                  style={{
                    background:    c.bg,
                    color:         c.color,
                    border:        `1px solid ${c.border}`,
                    padding:       "4px 12px",
                    borderRadius:  "999px",
                    fontSize:      "12px",
                    fontWeight:    "700",
                    cursor:        "pointer",
                    display:       "inline-block",
                    whiteSpace:    "nowrap",
                    outline:       isActive ? `2px solid ${c.color}` : "none",
                    outlineOffset: "2px",
                    transition:    "outline 0.15s",
                  }}
                >
                  {style}: {count}
                </span>
              );
            })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map((c) => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Dominant Style</label>
          <select
            value={filterStyle}
            onChange={(e) => setFilterStyle(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Styles</option>
            {uniqueStyles.map((s) => (
              <option key={s} value={s.toLowerCase()}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {enriched.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No 5S responses found.
        </div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No responses match your current filters.
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company</th>
                <th style={{ padding: "12px", minWidth: "260px" }}>Scores</th>
                <th style={{ padding: "12px", minWidth: "180px", background: "#fdf6e3" }}>
                  🎯 5S Results
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((resp, index) => {
                const results      = resp._results;
                const isGenerating = isGeneratingRow === index;
                const raw          = results?.rawScores || {};

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {resp.submission_time ? (
                        <>
                          {new Date(resp.submission_time).toLocaleDateString("en-US", {
                            month: "short", day: "numeric", year: "numeric",
                          })}
                          <br />
                          <span style={{ fontSize: "12px", color: "#888" }}>
                            {new Date(resp.submission_time).toLocaleTimeString("en-US", {
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      )}
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{resp.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {resp.email}<br />
                        {resp.phone || ""}
                      </span>
                    </td>

                    {/* ── Company ── */}
                    <td style={{ padding: "12px" }}>
                      {resp.custom_company
                        ? <b>{resp.custom_company}</b>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                    </td>

                    {/* ── Scores ── */}
                    <td style={{ padding: "12px" }}>
                      {results ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {[
                            { label: "Signaling",   key: "Signaling"           },
                            { label: "Stimulating", key: "Stimulating"         },
                            { label: "Suggesting",  key: "Suggesting"          },
                            { label: "Sharing",     key: "Sharing_Sanctioning" },
                          ].map(({ label, key }) => {
                            const c = SCORE_COLORS[key];
                            return (
                              <span
                                key={key}
                                style={{
                                  background:   c.bg,
                                  color:        c.color,
                                  border:       `1px solid ${c.border}`,
                                  padding:      "2px 8px",
                                  borderRadius: "4px",
                                  fontSize:     "12px",
                                  fontWeight:   "700",
                                  whiteSpace:   "nowrap",
                                }}
                              >
                                {label}: {raw[key] ?? 0}
                              </span>
                            );
                          })}
                          {/* Adaptability pill */}
                          {(() => {
                            const c = SCORE_COLORS.Total_Adaptability;
                            const val = raw.Total_Adaptability ?? 0;
                            return (
                              <span style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `1px solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   "700",
                                whiteSpace:   "nowrap",
                              }}>
                                Adapt: {val > 0 ? `+${val}` : val}
                              </span>
                            );
                          })()}
                        </div>
                      ) : (
                        <span style={{ color: "#e74c3c", fontSize: "12px" }}>Incomplete</span>
                      )}
                    </td>

                    {/* ── 5S Results ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      <DominantStylePill dominantStyle={results?.dominantStyle} />
                    </td>

                    {/* ── Actions ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(resp, results, index)}
                        disabled={isGenerating || !results}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating || !results ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}